import React, { useState, useMemo } from 'react';
import CalculatorLayout from '../../components/CalculatorLayout';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';

const BMICalculator = () => {
  const [unit, setUnit] = useState('metric');
  const [height, setHeight] = useState(170); // cm
  const [weight, setWeight] = useState(70); // kg
  const [feet, setFeet] = useState(5);
  const [inches, setInches] = useState(7);
  const [pounds, setPounds] = useState(154);
  const [age, setAge] = useState(28);
  const [gender, setGender] = useState('male');

  const { bmi, category, color, tip } = useMemo(() => {
    let h, w;
    if (unit === 'metric') { h = height / 100; w = weight; }
    else { h = (feet * 12 + parseFloat(inches || 0)) * 0.0254; w = pounds * 0.453592; }
    const b = w / (h * h);
    if (!isFinite(b) || b <= 0) return { bmi: 0, category: '—', color: 'slate', tip: '' };
    // Asian BMI thresholds (WHO for Asian populations)
    let cat, col, t;
    if (b < 18.5) { cat = 'Underweight'; col = 'saffron'; t = 'Consider increasing calorie intake with nutritious foods and consult a dietician.'; }
    else if (b < 23) { cat = 'Normal (Healthy)'; col = 'green'; t = 'You’re in a healthy range. Keep up a balanced diet and regular activity.'; }
    else if (b < 25) { cat = 'Overweight (Asian)'; col = 'saffron'; t = 'You may be at increased health risk per Asian BMI thresholds. Aim for moderate exercise.'; }
    else if (b < 30) { cat = 'Obese Class I'; col = 'saffron'; t = 'Consider lifestyle changes: exercise, diet, and a doctor consultation.'; }
    else { cat = 'Obese Class II+'; col = 'saffron'; t = 'Please consult a healthcare professional for a personalized plan.'; }
    return { bmi: b, category: cat, color: col, tip: t };
  }, [unit, height, weight, feet, inches, pounds]);

  const pct = Math.min(Math.max(((bmi - 10) / 30) * 100, 0), 100);

  return (
    <CalculatorLayout
      title="BMI Calculator"
      subtitle="Body Mass Index calculator with Indian (Asian) BMI thresholds."
      category="Fitness"
      description={<>
        <p>BMI (Body Mass Index) is a measure of body fat based on height and weight. For Indians and other Asian populations, the WHO recommends stricter thresholds because of higher health risks at lower BMI values.</p>
        <p><strong>Asian BMI ranges</strong>: Underweight &lt;18.5, Normal 18.5–22.9, Overweight 23–24.9, Obese ≥25.</p>
      </>}
      formulaNote="BMI = weight (kg) / height (m)²"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <Tabs value={unit} onValueChange={setUnit}>
            <TabsList className="grid grid-cols-2 w-full">
              <TabsTrigger value="metric">Metric (cm, kg)</TabsTrigger>
              <TabsTrigger value="imperial">Imperial (ft, lb)</TabsTrigger>
            </TabsList>
            <TabsContent value="metric" className="space-y-4 mt-4">
              <InputRow label="Height" unit="cm" value={height} onChange={setHeight} />
              <InputRow label="Weight" unit="kg" value={weight} onChange={setWeight} />
            </TabsContent>
            <TabsContent value="imperial" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-3">
                <InputRow label="Height (ft)" unit="ft" value={feet} onChange={setFeet} />
                <InputRow label="(inches)" unit="in" value={inches} onChange={setInches} />
              </div>
              <InputRow label="Weight" unit="lb" value={pounds} onChange={setPounds} />
            </TabsContent>
          </Tabs>

          <div className="grid grid-cols-2 gap-3 mt-4">
            <InputRow label="Age" unit="yr" value={age} onChange={setAge} />
            <div>
              <Label className="text-sm font-medium text-slate-700 mb-2 block">Gender</Label>
              <RadioGroup value={gender} onValueChange={setGender} className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <RadioGroupItem value="male" /> <span className="text-sm">Male</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <RadioGroupItem value="female" /> <span className="text-sm">Female</span>
                </label>
              </RadioGroup>
            </div>
          </div>
        </div>

        <div className={`rounded-xl border border-slate-200 p-5 bg-gradient-to-br ${
          color === 'green' ? 'from-indgreen-50 to-white' : color === 'saffron' ? 'from-saffron-50 to-white' : 'from-slate-50 to-white'
        }`}>
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Your BMI</div>
          <div className="font-display text-5xl font-bold text-bharat-navy display-font mt-1">{bmi.toFixed(1)}</div>
          <div className={`inline-block mt-2 px-3 py-1 rounded-full text-sm font-semibold ${
            color === 'green' ? 'bg-indgreen-500 text-white' : color === 'saffron' ? 'bg-saffron-500 text-white' : 'bg-slate-300 text-slate-700'
          }`}>{category}</div>

          {/* Range bar */}
          <div className="mt-5">
            <div className="h-3 rounded-full flex overflow-hidden">
              <div className="flex-[8.5] bg-amber-300" title="Underweight" />
              <div className="flex-[4.5] bg-indgreen-500" title="Normal" />
              <div className="flex-[2] bg-saffron-400" title="Overweight" />
              <div className="flex-[5] bg-saffron-600" title="Obese I" />
              <div className="flex-[10] bg-red-500" title="Obese II+" />
            </div>
            <div className="relative h-6">
              <div className="absolute top-0 w-0.5 h-4 bg-bharat-navy" style={{ left: `calc(${pct}% - 1px)` }} />
              <div className="absolute top-4 text-[10px] font-semibold text-bharat-navy display-font" style={{ left: `calc(${pct}% - 10px)` }}>{bmi.toFixed(1)}</div>
            </div>
            <div className="flex justify-between text-[10px] text-slate-500 mt-0">
              <span>10</span><span>18.5</span><span>23</span><span>25</span><span>30</span><span>40</span>
            </div>
          </div>

          <p className="text-sm text-slate-700 mt-4 leading-relaxed">{tip}</p>
        </div>
      </div>
    </CalculatorLayout>
  );
};

const InputRow = ({ label, unit, value, onChange }) => (
  <div>
    <Label className="text-sm font-medium text-slate-700 mb-1.5 block">{label}</Label>
    <div className="relative">
      <Input type="number" value={value} onChange={(e) => onChange(+e.target.value)} className="pr-12" />
      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-500 font-medium">{unit}</span>
    </div>
  </div>
);

export default BMICalculator;
